/**
 * `startProperties` — the entry hand-off from the external marketing site.
 *
 * A new owner lands on the configurator carrying `?startProperties=<base64-JSON>`, e.g.
 *
 *   { "consent": { "required": true, "performance": true, "marketing": false }, "locale": "mx" }
 *
 * This module DECODES + VALIDATES that param (pure, side-effect-free). Consuming it — apply
 * the locale, save it to user-preferences, persist the consent — is done by
 * `./useStartProperties.ts`. Anything absent or malformed is dropped, never guessed.
 */
import { parseTracking, type StartTracking } from '@/utils/start-tracking';

import type { TrackingConsent } from './analytics/consent';

/** Query-param name carrying the base64-encoded hand-off object. */
export const START_PROPERTIES_PARAM = 'startProperties';

/**
 * `sessionStorage` key the boot shim (`src/dev.js`) stashes the RAW param under, so the
 * hand-off survives the configurator's auth gate bouncing an unauthenticated arrival to
 * `/login` (that redirect drops the query string).
 */
export const START_PROPERTIES_STASH_KEY = 'ows-start-properties';

/**
 * `sessionStorage` key recording the locale a `startProperties` hand-off applied this session.
 * Set at boot (`src/dev.js`) and on consume (`./useStartProperties.ts`); read by the app-wide
 * theme provider (`nova/theme/OwsTheme.tsx`) so its saved-language adoption does NOT revert the
 * hand-off override. Session-scoped and deliberately NOT cleared mid-session (unlike the raw
 * stash, which is one-shot). The literal key is mirrored in dev.js + OwsTheme (plain-JS / no-import).
 */
export const START_LOCALE_OVERRIDE_KEY = 'ows-start-locale-override';

/** Max accepted raw-param length — spec §5.3 recommends ~16 KB. Reject a giant (hostile) value
 *  rather than decode/persist it. Mirrored in dev.js + `src/utils/start-tracking.ts`. */
const MAX_RAW_LENGTH = 16 * 1024;

/**
 * The hand-off comes from an EXTERNAL site, so every string field is untrusted. We accept
 * only tightly-bounded ASCII allowlists and DROP anything else, so a hostile value can't
 * smuggle an injection payload (markup, quotes, entities, path traversal, control/unicode
 * tricks) into localStorage/sessionStorage, an i18n key, or any later sink. Downstream
 * consumers still validate at use (supported-language check, campaign-name match), so this
 * is defence-in-depth, not the only guard. Patterns are MIRRORED in `src/dev.js`.
 */
/** A locale/language code: BCP-47-ish (letters + optional `-`/`_` subtags), length-bounded. */
const SAFE_LOCALE_RE = /^[A-Za-z]{2,3}(?:[-_][A-Za-z0-9]{2,8})*$/;
const isSafeLocale = (v: string): boolean => v.length <= 35 && SAFE_LOCALE_RE.test(v);
/** A pack / campaign-name slug: alphanumerics + spaces/underscore/hyphen, length-bounded. */
const SAFE_PACKAGE_RE = /^[A-Za-z0-9][A-Za-z0-9 _-]{0,39}$/;
const isSafePackage = (v: string): boolean => SAFE_PACKAGE_RE.test(v);
/** A voucher / promo code: alphanumerics + underscore/hyphen (NO spaces), length-bounded. */
const SAFE_VOUCHER_RE = /^[A-Za-z0-9][A-Za-z0-9_-]{0,63}$/;
/** Shape guard for a voucher / promo code (also reused by the checkout-review editable field to
 *  reject obviously-malformed input before a `/session/save` round-trip). */
export const isSafeVoucher = (v: string): boolean => SAFE_VOUCHER_RE.test(v);
/** Upper bound for a catalogue id — a generous ceiling on real seed/skin/art-style ids (which are
 *  small). Applied IDENTICALLY on both toCatalogId paths and mirrored by dev.js `isSafeId`, so a
 *  boot-stashed value and its re-read agree exactly (incl. at the boundary). */
const MAX_CATALOG_ID = 999_999_999;
/**
 * A configurator catalogue id (site_seed / site_skin / site_art_style): a bounded POSITIVE
 * integer. Accepts a number (from the decoded JSON hand-off) or a numeric string (from the
 * boot-stashed durable sessionStorage key), and rejects anything else (non-integer, <= 0, or
 * above {@link MAX_CATALOG_ID}) — so a hostile / malformed id can never reach the `session/save`
 * payload. Returns `null` when unusable.
 */
const toCatalogId = (v: unknown): number | null => {
  const n =
    typeof v === 'number'
      ? v
      : typeof v === 'string' && /^\d{1,9}$/.test(v.trim())
        ? Number(v.trim())
        : Number.NaN;
  return Number.isInteger(n) && n > 0 && n <= MAX_CATALOG_ID ? n : null;
};

/**
 * The marketing-site "template" hand-off: a pre-picked design chosen upstream, carried as the
 * public configurator catalogue ids `site_seed_id` / `site_skin_id` / `site_art_style_id`. The
 * configurator pre-selects + persists this design on a fresh session. Each field is `null` when
 * absent/invalid, so a PARTIAL template (e.g. seed only) is honoured field-by-field, with catalogue
 * defaults filling the rest.
 */
export interface StartTemplate {
  /** site_seed id — matched against `Theme.seedId`. */
  siteSeedId: number | null;
  /** site_skin id — matched against `ColorOption.id`. */
  siteSkinId: number | null;
  /** site_art_style id — matched against `ArtStyleOption.id`. */
  artStyleId: number | null;
}

/**
 * The decoded `startProperties` hand-off. Every field is optional — the marketing site may
 * send only a locale, only consent, or both.
 */
export interface StartProperties {
  /** Preferred UI language (an ISO-ish code, e.g. `en` / `mx`). */
  locale?: string;
  /** The owner's tracking-consent choice, to gate the tracking scripts wired up later. */
  consent?: TrackingConsent;
  /**
   * A pack to pre-select in the configurator, by campaign NAME (e.g. `basic` / `focused` /
   * `edge` / `superstar`) — matched case-insensitively against the market's available packs.
   * Only a starting cue: applied once on a fresh session, never over an engaged one.
   */
  selectedPackage?: string;
  /**
   * A marketing-site voucher / promo code (e.g. an affiliate voucher). Threaded to the backend so
   * the session applies the voucher: seeded onto the `/session/load` mint, persisted in
   * `configuration_data.voucher_code` on every `/session/save`, and redeemed at `/session/invoice`.
   * The applied discount comes back on the session's top-level `voucher` object and is shown in the
   * checkout review. Only a starting cue: consumed once persisted onto the session.
   */
  voucherCode?: string;
  /**
   * A pre-picked design (seed / skin / art style) to pre-select + persist on a fresh session.
   * Only a starting cue: a saved/resumed design always wins, and it's consumed once applied.
   */
  template?: StartTemplate;
  /**
   * The marketing site's ad-attribution capture (ad-network click-ids + `utm_*` + first-touch
   * metadata), sanitized at decode. Persisted once as an immutable first-touch snapshot. Absent on
   * a direct / non-ad visit.
   */
  tracking?: StartTracking;
}

/**
 * UTF-8-safe base64 decode that also tolerates URL-safe base64 and missing padding.
 * A `+` in a query string is decoded to a space by `URLSearchParams`, and the sender may
 * use either standard (`+` `/`) or URL-safe (`-` `_`) base64 — normalise all of them.
 *
 * NOTE: this normalisation is MIRRORED in `src/dev.js` (plain JS, can't import this module) —
 * keep both in sync if the decode rules ever change.
 */
function decodeBase64Utf8(raw: string): string {
  let b64 = raw.replace(/ /g, '+').replace(/-/g, '+').replace(/_/g, '/');
  const pad = b64.length % 4;
  if (pad) b64 += '='.repeat(4 - pad);
  const binary = atob(b64);
  const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

/**
 * Narrow an unknown decoded value to a {@link StartTemplate}, or `undefined` when it carries no
 * usable id. Each id is validated independently, so a partial `template` still yields a template
 * (the missing ids stay `null` and fall back to catalogue defaults downstream).
 */
function parseTemplate(value: unknown): StartTemplate | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const t = value as Record<string, unknown>;
  const siteSeedId = toCatalogId(t.site_seed_id);
  const siteSkinId = toCatalogId(t.site_skin_id);
  const artStyleId = toCatalogId(t.site_art_style_id);
  if (siteSeedId == null && siteSkinId == null && artStyleId == null) return undefined;
  return { siteSeedId, siteSkinId, artStyleId };
}

/** Narrow an unknown decoded value to a {@link TrackingConsent}, or `undefined`. */
function parseConsent(value: unknown): TrackingConsent | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const c = value as Record<string, unknown>;
  return {
    required: c.required === true,
    performance: c.performance === true,
    marketing: c.marketing === true,
  };
}

/**
 * Decode + validate a raw `startProperties` value (the percent-decoded base64 string).
 * Returns `null` for anything absent or malformed — a broken hand-off must never throw.
 */
export function parseStartProperties(raw: string | null | undefined): StartProperties | null {
  if (!raw || raw.length > MAX_RAW_LENGTH) return null;
  try {
    const decoded: unknown = JSON.parse(decodeBase64Utf8(raw));
    if (!decoded || typeof decoded !== 'object') return null;
    const obj = decoded as Record<string, unknown>;
    const result: StartProperties = {};
    if (typeof obj.locale === 'string') {
      const locale = obj.locale.trim();
      if (locale && isSafeLocale(locale)) result.locale = locale;
    }
    const consent = parseConsent(obj.consent);
    if (consent) result.consent = consent;
    if (typeof obj.selected_package === 'string') {
      const pkg = obj.selected_package.trim();
      if (pkg && isSafePackage(pkg)) result.selectedPackage = pkg;
    }
    if (typeof obj.voucher_code === 'string') {
      const voucher = obj.voucher_code.trim();
      if (voucher && isSafeVoucher(voucher)) result.voucherCode = voucher;
    }
    const template = parseTemplate(obj.template);
    if (template) result.template = template;
    const tracking = parseTracking(obj.tracking);
    if (tracking) result.tracking = tracking;
    return Object.keys(result).length ? result : null;
  } catch {
    return null;
  }
}

/** Raw `startProperties` value from the URL, else the boot-time sessionStorage stash. */
export function readRawStartProperties(): string | null {
  try {
    const fromUrl = new URLSearchParams(window.location.search).get(START_PROPERTIES_PARAM);
    if (fromUrl) return fromUrl;
  } catch {
    /* URL unparseable — fall through to the stash. */
  }
  try {
    return sessionStorage.getItem(START_PROPERTIES_STASH_KEY);
  } catch {
    return null;
  }
}

/** Clear the one-shot sessionStorage stash after the hand-off has been consumed. */
export function clearStartPropertiesStash(): void {
  try {
    sessionStorage.removeItem(START_PROPERTIES_STASH_KEY);
  } catch {
    /* nothing to clear. */
  }
}

/**
 * Record that a `startProperties` locale override is active this session, so the app-wide
 * saved-language adoption (`nova/theme/OwsTheme.tsx`) won't revert it. Best-effort — if
 * sessionStorage is unavailable, adoption simply isn't suppressed. See {@link START_LOCALE_OVERRIDE_KEY}.
 */
export function markStartLocaleOverride(locale: string): void {
  try {
    sessionStorage.setItem(START_LOCALE_OVERRIDE_KEY, locale);
  } catch {
    /* sessionStorage unavailable — override just isn't flagged (best effort). */
  }
}

/**
 * `sessionStorage` key holding the pack the `startProperties` hand-off asked to pre-select
 * (a campaign name, e.g. `superstar`). The boot shim (`src/dev.js`) stashes it so it's
 * available from the first render and survives the auth gate + the raw stash being consumed
 * (like the flow + locale keys); the configurator store reads it once, applies the pack on a
 * fresh session, and clears it so it doesn't re-apply to a later brand. The literal is
 * mirrored in `src/dev.js` (plain JS can't import this).
 */
export const START_SELECTED_PACKAGE_KEY = 'ows-start-selected-package';

/** The pack name the `startProperties` hand-off asked to pre-select this session, or `null`. */
export function readStartSelectedPackage(): string | null {
  try {
    return sessionStorage.getItem(START_SELECTED_PACKAGE_KEY);
  } catch {
    return null;
  }
}

/** Consume the one-shot pre-select-pack hand-off so it isn't re-applied to a later brand. */
export function clearStartSelectedPackage(): void {
  try {
    sessionStorage.removeItem(START_SELECTED_PACKAGE_KEY);
  } catch {
    /* nothing to clear. */
  }
}

/**
 * `sessionStorage` key holding the voucher / promo code the `startProperties` hand-off carried.
 * The boot shim (`src/dev.js`) stashes it so it's available from the first render and survives the
 * auth-gate `/login` redirect (which drops the query string) AND the one-shot raw-`startProperties`
 * stash being consumed. The configurator store reads it once, seeds it onto the session (`/load`
 * mint + `configuration_data` on save), then clears it so it can't re-apply to a later brand. The
 * literal is mirrored in `src/dev.js` (plain JS can't import this).
 */
export const START_VOUCHER_CODE_KEY = 'ows-start-voucher-code';

/**
 * Query-param name carrying a STANDALONE voucher / promo code, interpreted exactly like
 * `startProperties.voucher_code`. When BOTH are present the standalone param WINS (mirrored in
 * `src/dev.js`, which stashes the standalone value over the embedded one at boot).
 */
export const VOUCHER_CODE_PARAM = 'voucher_code';

/** The standalone `?voucher_code=` URL param this session, validated, or `null`. */
function readVoucherCodeParam(): string | null {
  try {
    const raw = new URLSearchParams(window.location.search).get(VOUCHER_CODE_PARAM);
    const voucher = raw?.trim();
    return voucher && isSafeVoucher(voucher) ? voucher : null;
  } catch {
    return null;
  }
}

/**
 * The voucher code this session, or `null`. A STANDALONE `?voucher_code=` URL param WINS over the
 * `startProperties`-embedded one; both are re-validated on read (a corrupt/oversized value drops to
 * `null`, so a hostile stash can't reach the session body). Reads the live URL param first, then the
 * boot stash (which `src/dev.js` already resolves standalone-wins, so it survives the auth-gate
 * `/login` redirect that drops the query string).
 */
export function readStartVoucherCode(): string | null {
  const fromParam = readVoucherCodeParam();
  if (fromParam) return fromParam;
  try {
    const raw = sessionStorage.getItem(START_VOUCHER_CODE_KEY);
    const voucher = raw?.trim();
    return voucher && isSafeVoucher(voucher) ? voucher : null;
  } catch {
    return null;
  }
}

/** Consume the one-shot voucher hand-off so it isn't re-applied to a later brand. */
export function clearStartVoucherCode(): void {
  try {
    sessionStorage.removeItem(START_VOUCHER_CODE_KEY);
  } catch {
    /* nothing to clear. */
  }
}

/**
 * Durable per-field `sessionStorage` keys the boot shim (`src/dev.js`) pre-extracts the `template`
 * hand-off into (the pre-picked seed / skin / art-style ids). Like the pack + market keys, they
 * survive the auth-gate `/login` redirect (which drops the query string) AND the one-shot
 * raw-`startProperties` stash being consumed. The configurator store reads them once, seeds +
 * persists the design on a fresh session, then clears them so they can't re-apply to a later
 * brand. Literals are MIRRORED in `src/dev.js` (plain JS can't import this).
 */
export const START_TEMPLATE_SEED_KEY = 'ows-start-template-seed';
export const START_TEMPLATE_SKIN_KEY = 'ows-start-template-skin';
export const START_TEMPLATE_ART_STYLE_KEY = 'ows-start-template-art-style';

/**
 * The design (seed / skin / art style) the `startProperties` template asked to pre-select this
 * session, or `null` when none was stashed. Reads the durable per-field keys set at boot; each id
 * is re-validated (a corrupt value drops to `null`). Returns `null` only when ALL three are absent.
 */
export function readStartTemplate(): StartTemplate | null {
  const read = (key: string): number | null => {
    try {
      return toCatalogId(sessionStorage.getItem(key));
    } catch {
      return null;
    }
  };
  const siteSeedId = read(START_TEMPLATE_SEED_KEY);
  const siteSkinId = read(START_TEMPLATE_SKIN_KEY);
  const artStyleId = read(START_TEMPLATE_ART_STYLE_KEY);
  if (siteSeedId == null && siteSkinId == null && artStyleId == null) return null;
  return { siteSeedId, siteSkinId, artStyleId };
}

/** Consume the one-shot template hand-off so it isn't re-applied to a later brand. */
export function clearStartTemplate(): void {
  try {
    sessionStorage.removeItem(START_TEMPLATE_SEED_KEY);
    sessionStorage.removeItem(START_TEMPLATE_SKIN_KEY);
    sessionStorage.removeItem(START_TEMPLATE_ART_STYLE_KEY);
  } catch {
    /* nothing to clear. */
  }
}
